import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { config } from '../config/config';
import NavBar from '../components/NavBar';
import Footer from '../components/Footer';
import '../styles/AdminPage.css';

const AdminPage = () => {
  const navigate = useNavigate();
  const auth = getAuth();
  const user = auth.currentUser;
  
  const [activeTab, setActiveTab] = useState('payments');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Payment state
  const [payments, setPayments] = useState([]);
  const [paymentFilter, setPaymentFilter] = useState('PENDING');
  const [selectedPayment, setSelectedPayment] = useState(null);
  
  // Users state
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [emailListOpen, setEmailListOpen] = useState(false);
  const [emailListCopied, setEmailListCopied] = useState(false);

  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
    } else {
      checkAdminAccess();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  useEffect(() => {
    if (isAdmin && activeTab === 'payments') {
      loadPayments();
    } else if (isAdmin && activeTab === 'analytics') {
      loadAnalytics();
    } else if (isAdmin && activeTab === 'users') {
      loadUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, activeTab, paymentFilter, dateRange]);

  const checkAdminAccess = async () => {
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${config.apiUrl}/admin/analytics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.status === 403) {
        setError('Access Denied. You do not have admin privileges.');
        setIsAdmin(false);
        setTimeout(() => navigate('/'), 3000);
        return;
      }
      
      if (!response.ok) {
        throw new Error('Failed to verify admin access');
      }
      
      setIsAdmin(true);
      setError(null);
    } catch (err) {
      console.error('Admin access check failed:', err);
      setError('Failed to verify admin access. Redirecting...');
      setTimeout(() => navigate('/'), 3000);
    } finally {
      setLoading(false);
    }
  };

  const loadPayments = async () => {
    try {
      setLoading(true);
      const token = await user.getIdToken();
      const response = await fetch(
        `${config.apiUrl}/admin/payments?status=${paymentFilter}&limit=50`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (!response.ok) throw new Error('Failed to load payments');
      
      const data = await response.json();
      setPayments(data.payments);
      setError(null);
    } catch (err) {
      console.error('Error loading payments:', err);
      setError('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const token = await user.getIdToken();
      const response = await fetch(`${config.apiUrl}/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to load users');
      const data = await response.json();
      setUsers(data.users);
      setError(null);
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredUsers = () =>
    users.filter(u =>
      u.email.toLowerCase().includes(userSearch.toLowerCase()) ||
      `${u.firstName} ${u.lastName}`.toLowerCase().includes(userSearch.toLowerCase())
    );

  const copyEmailList = () => {
    const emails = getFilteredUsers().map(u => u.email).join(';');
    navigator.clipboard.writeText(emails).then(() => {
      setEmailListCopied(true);
      setTimeout(() => setEmailListCopied(false), 2500);
    });
  };

  const exportUsersCSV = () => {
    const filtered = getFilteredUsers();
    const header = 'First Name,Last Name,Email,Joined';
    const rows = filtered.map(u => {
      const joined = u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '';
      return [
        `"${u.firstName}"`,
        `"${u.lastName}"`,
        `"${u.email}"`,
        `"${joined}"`
      ].join(',');
    });
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const token = await user.getIdToken();
      const response = await fetch(
        `${config.apiUrl}/admin/analytics?startDate=${dateRange.start}&endDate=${dateRange.end}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (!response.ok) throw new Error('Failed to load analytics');
      
      const data = await response.json();
      setAnalytics(data);
      setError(null);
    } catch (err) {
      console.error('Error loading analytics:', err);
      setError('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleApprovePayment = async (paymentId) => {
    if (!window.confirm('Are you sure you want to approve this payment?')) return;
    
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${config.apiUrl}/admin/payments/${paymentId}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ notes: 'Manually approved by admin' })
      });
      
      if (!response.ok) throw new Error('Failed to approve payment');
      
      alert('Payment approved successfully!');
      setSelectedPayment(null);
      loadPayments();
    } catch (err) {
      console.error('Error approving payment:', err);
      alert('Failed to approve payment');
    }
  };

  const handleRejectPayment = async (paymentId) => {
    const reason = window.prompt('Enter rejection reason:');
    if (!reason) return;
    
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${config.apiUrl}/admin/payments/${paymentId}/reject`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason })
      });
      
      if (!response.ok) throw new Error('Failed to reject payment');
      
      alert('Payment rejected successfully!');
      setSelectedPayment(null);
      loadPayments();
    } catch (err) {
      console.error('Error rejecting payment:', err);
      alert('Failed to reject payment');
    }
  };

  if (loading && !isAdmin) {
    return (
      <div className="admin-page">
        <NavBar />
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Verifying admin access...</p>
        </div>
      </div>
    );
  }

  if (error && !isAdmin) {
    return (
      <div className="admin-page">
        <NavBar />
        <div className="error-container">
          <i className="fas fa-exclamation-triangle"></i>
          <h2>{error}</h2>
          <p>Redirecting to home page...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <NavBar />
      <div className="admin-container">
        <div className="admin-header">
          <h1><i className="fas fa-shield-alt"></i> Admin Dashboard</h1>
          <p>Manage payments and view analytics</p>
        </div>

        <div className="admin-tabs">
          <button 
            className={`tab-button ${activeTab === 'payments' ? 'active' : ''}`}
            onClick={() => setActiveTab('payments')}
          >
            <i className="fas fa-money-check-alt"></i> Payments
          </button>
          <button 
            className={`tab-button ${activeTab === 'analytics' ? 'active' : ''}`}
            onClick={() => setActiveTab('analytics')}
          >
            <i className="fas fa-chart-line"></i> Analytics
          </button>
          <button 
            className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            <i className="fas fa-users"></i> Users
          </button>
        </div>

        {activeTab === 'payments' && (
          <div className="payments-section">
            <div className="section-header">
              <h2>Payment Management</h2>
              <div className="filter-controls">
                <label>Filter by Status:</label>
                <select 
                  value={paymentFilter} 
                  onChange={(e) => setPaymentFilter(e.target.value)}
                >
                  <option value="ALL">All Payments</option>
                  <option value="PENDING">Pending</option>
                  <option value="APPROVED">Approved</option>
                  <option value="REJECTED">Rejected</option>
                </select>
              </div>
            </div>

            {loading ? (
              <div className="loading-spinner"></div>
            ) : payments.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-inbox"></i>
                <p>No payments found</p>
              </div>
            ) : (
              <div className="payments-grid">
                {payments.map(payment => (
                  <div key={payment.id} className={`payment-card ${payment.status.toLowerCase()}`}>
                    <div className="payment-header">
                      <span className={`status-badge ${payment.status.toLowerCase()}`}>
                        {payment.status}
                      </span>
                      <span className="payment-date">
                        {new Date(payment.submittedAt).toLocaleDateString()}
                      </span>
                    </div>
                    
                    <div className="payment-info">
                      <div className="info-row">
                        <label>User:</label>
                        <span>{payment.userInfo?.email || payment.userId}</span>
                      </div>
                      <div className="info-row">
                        <label>Plan:</label>
                        <span>{payment.subscriptionPlan}</span>
                      </div>
                      <div className="info-row">
                        <label>Amount:</label>
                        <span className="amount">₱{payment.amount}</span>
                      </div>
                      <div className="info-row">
                        <label>Method:</label>
                        <span>{payment.method?.toUpperCase()}</span>
                      </div>
                    </div>

                    <div className="payment-screenshot">
                      <img 
                        src={payment.paymentScreenshotUrl} 
                        alt="Payment Screenshot"
                        onClick={() => setSelectedPayment(payment)}
                      />
                      <button 
                        className="view-btn"
                        onClick={() => setSelectedPayment(payment)}
                      >
                        <i className="fas fa-eye"></i> View Details
                      </button>
                    </div>

                    {payment.status === 'PENDING' && (
                      <div className="payment-actions">
                        <button 
                          className="approve-btn"
                          onClick={() => handleApprovePayment(payment.id)}
                        >
                          <i className="fas fa-check"></i> Approve
                        </button>
                        <button 
                          className="reject-btn"
                          onClick={() => handleRejectPayment(payment.id)}
                        >
                          <i className="fas fa-times"></i> Reject
                        </button>
                      </div>
                    )}

                    {payment.validationResult && (
                      <div className="ai-validation">
                        <p className="confidence">
                          AI Confidence: {(payment.validationResult.confidence * 100).toFixed(1)}%
                        </p>
                        <p className="reason">{payment.validationResult.reason}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="analytics-section">
            <div className="section-header">
              <h2>Usage Analytics</h2>
              <div className="date-range-controls">
                <label>Date Range:</label>
                <input 
                  type="date" 
                  value={dateRange.start}
                  onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
                />
                <span>to</span>
                <input 
                  type="date" 
                  value={dateRange.end}
                  onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
                />
              </div>
            </div>

            {loading ? (
              <div className="loading-spinner"></div>
            ) : analytics ? (
              <>
                <div className="stats-grid">
                  <div className="stat-card revenue">
                    <i className="fas fa-dollar-sign"></i>
                    <h3>Total Revenue</h3>
                    <p className="stat-value">₱{analytics.payments.totalRevenue.toLocaleString()}</p>
                  </div>
                  
                  <div className="stat-card payments">
                    <i className="fas fa-receipt"></i>
                    <h3>Total Payments</h3>
                    <p className="stat-value">{analytics.payments.total}</p>
                    <div className="stat-breakdown">
                      <span className="approved">Approved: {analytics.payments.approved}</span>
                      <span className="pending">Pending: {analytics.payments.pending}</span>
                      <span className="rejected">Rejected: {analytics.payments.rejected}</span>
                    </div>
                  </div>
                  
                  <div className="stat-card sessions">
                    <i className="fas fa-play-circle"></i>
                    <h3>Total Sessions</h3>
                    <p className="stat-value">{analytics.sessions.total}</p>
                    <div className="stat-breakdown">
                      <span className="active">Active: {analytics.sessions.active}</span>
                      <span className="expired">Expired: {analytics.sessions.expired}</span>
                    </div>
                  </div>
                  
                  <div className="stat-card subscriptions">
                    <i className="fas fa-users"></i>
                    <h3>Active Subscriptions</h3>
                    <p className="stat-value">{analytics.subscriptions.active}</p>
                  </div>
                </div>

                <div className="breakdown-section">
                  <div className="breakdown-card">
                    <h3>Revenue by Plan</h3>
                    <div className="breakdown-list">
                      {Object.entries(analytics.payments.revenueByPlan).map(([plan, revenue]) => (
                        <div key={plan} className="breakdown-item">
                          <span className="plan-name">{plan}</span>
                          <span className="plan-value">₱{revenue.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="breakdown-card">
                    <h3>Sessions by Plan</h3>
                    <div className="breakdown-list">
                      {Object.entries(analytics.sessions.byPlan).map(([plan, count]) => (
                        <div key={plan} className="breakdown-item">
                          <span className="plan-name">{plan}</span>
                          <span className="plan-value">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="breakdown-card">
                    <h3>Active Subscriptions by Plan</h3>
                    <div className="breakdown-list">
                      {Object.entries(analytics.subscriptions.byPlan).map(([plan, count]) => (
                        <div key={plan} className="breakdown-item">
                          <span className="plan-name">{plan}</span>
                          <span className="plan-value">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="empty-state">
                <i className="fas fa-chart-line"></i>
                <p>No analytics data available</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'users' && (
          <div className="users-section">
            <div className="section-header">
              <h2>User Export</h2>
              <div className="user-controls">
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="user-search-input"
                />
                <button className="export-btn" onClick={exportUsersCSV} disabled={users.length === 0}>
                  <i className="fas fa-download"></i> Export CSV
                </button>
                <button
                  className="broadcast-toggle-btn"
                  onClick={() => setEmailListOpen(o => !o)}
                  disabled={users.length === 0}
                >
                  <i className="fas fa-bullhorn"></i> {emailListOpen ? 'Hide' : 'Broadcast List'}
                </button>
              </div>
            </div>

            {emailListOpen && (
              <div className="broadcast-panel">
                <div className="broadcast-panel-header">
                  <span>
                    <i className="fas fa-envelope"></i> Semicolon-separated emails
                    {userSearch && <span className="broadcast-filtered-note"> (filtered)</span>}
                  </span>
                  <button
                    className={`copy-emails-btn ${emailListCopied ? 'copied' : ''}`}
                    onClick={copyEmailList}
                  >
                    <i className={`fas ${emailListCopied ? 'fa-check' : 'fa-copy'}`}></i>
                    {emailListCopied ? ' Copied!' : ' Copy'}
                  </button>
                </div>
                <textarea
                  className="broadcast-textarea"
                  readOnly
                  value={getFilteredUsers().map(u => u.email).join(';')}
                />
              </div>
            )}

            {loading ? (
              <div className="loading-spinner"></div>
            ) : users.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-users"></i>
                <p>No users found</p>
              </div>
            ) : (
              <>
                <p className="user-count">
                  Showing {getFilteredUsers().length} of {users.length} users
                </p>
                <div className="users-table-wrapper">
                  <table className="users-table">
                    <thead>
                      <tr>
                        <th>First Name</th>
                        <th>Last Name</th>
                        <th>Email</th>
                        <th>Joined</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredUsers().map(u => (
                          <tr key={u.id}>
                            <td>{u.firstName}</td>
                            <td>{u.lastName}</td>
                            <td>{u.email}</td>
                            <td>{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {selectedPayment && (
          <div className="modal-overlay" onClick={() => setSelectedPayment(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setSelectedPayment(null)}>
                <i className="fas fa-times"></i>
              </button>
              
              <h2>Payment Details</h2>
              
              <div className="modal-screenshot">
                <img src={selectedPayment.paymentScreenshotUrl} alt="Payment Screenshot" />
              </div>
              
              <div className="modal-info">
                <div className="info-row">
                  <label>Status:</label>
                  <span className={`status-badge ${selectedPayment.status.toLowerCase()}`}>
                    {selectedPayment.status}
                  </span>
                </div>
                <div className="info-row">
                  <label>User:</label>
                  <span>{selectedPayment.userInfo?.email || selectedPayment.userId}</span>
                </div>
                <div className="info-row">
                  <label>Plan:</label>
                  <span>{selectedPayment.subscriptionPlan}</span>
                </div>
                <div className="info-row">
                  <label>Duration:</label>
                  <span>{selectedPayment.subscriptionDuration} days</span>
                </div>
                <div className="info-row">
                  <label>Amount:</label>
                  <span className="amount">₱{selectedPayment.amount}</span>
                </div>
                <div className="info-row">
                  <label>Method:</label>
                  <span>{selectedPayment.method?.toUpperCase()}</span>
                </div>
                <div className="info-row">
                  <label>Submitted:</label>
                  <span>{new Date(selectedPayment.submittedAt).toLocaleString()}</span>
                </div>
                {selectedPayment.reviewedAt && (
                  <div className="info-row">
                    <label>Reviewed:</label>
                    <span>{new Date(selectedPayment.reviewedAt).toLocaleString()}</span>
                  </div>
                )}
                {selectedPayment.notes && (
                  <div className="info-row">
                    <label>Notes:</label>
                    <span>{selectedPayment.notes}</span>
                  </div>
                )}
              </div>

              {selectedPayment.status === 'PENDING' && (
                <div className="modal-actions">
                  <button 
                    className="approve-btn"
                    onClick={() => handleApprovePayment(selectedPayment.id)}
                  >
                    <i className="fas fa-check"></i> Approve Payment
                  </button>
                  <button 
                    className="reject-btn"
                    onClick={() => handleRejectPayment(selectedPayment.id)}
                  >
                    <i className="fas fa-times"></i> Reject Payment
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default AdminPage;
